import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import styled from "styled-components"
import { User, Mail, Lock, Eye, EyeOff, AlertCircle, X } from "lucide-react"
import { useAuth } from "../context/AuthContext"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 64px);
  padding: 20px;
`

const RegisterCard = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`

const RegisterHeader = styled.div`
  padding: 24px;
  text-align: center;
  border-bottom: 1px solid #eee;
`

const Logo = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  
  span:first-child {
    color: #333;
  }
  
  span:last-child {
    color: #2563eb;
  }
`

const Subtitle = styled.p`
  color: #666;
  margin: 8px 0 0 0;
  font-size: 14px;
`

const RegisterContent = styled.div`
  padding: 24px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`

const InputWrapper = styled.div`
  position: relative;
`

const Input = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid ${(props) => (props.$error ? "#ef4444" : "#ddd")};
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$error ? "#ef4444" : "#2563eb")};
  }
`

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #666;
  }
`

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const SubmitButton = styled.button`
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 8px;

  &:hover {
    background-color: #1d4ed8;
  }
  
  &:disabled {
    background-color: #93c5fd;
    cursor: not-allowed;
  }
`

const LoginLink = styled.div`
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #666;
  
  a {
    color: #2563eb;
    font-weight: 500;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`

const TermsAgreement = styled.div`
  margin-top: 16px;
  font-size: 12px;
  color: #666;
  text-align: center;
  
  a {
    color: #2563eb;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`
const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  z-index: 10;
`
export default function RegisterPage({onClose}) {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  })
  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { login } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // 입력 시 해당 필드의 에러 메시지 제거
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.username.trim()) {
      newErrors.username = "아이디를 입력해주세요"
    } else if (formData.username.length < 4) {
      newErrors.username = "아이디는 4자 이상이어야 합니다"
    }

    if (!formData.email.trim()) {
      newErrors.email = "이메일을 입력해주세요"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "유효한 이메일 주소를 입력해주세요"
    }

    if (!formData.password) {
      newErrors.password = "비밀번호를 입력해주세요"
    } else if (formData.password.length < 6) {
      newErrors.password = "비밀번호는 6자 이상이어야 합니다"
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호 확인을 입력해주세요"
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호가 일치하지 않습니다"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // RegisterPage.jsx 수정

const handleSubmit = async (e) => {
  e.preventDefault()

  if (!validateForm()) {
    return
  }

  setIsSubmitting(true)

  try {
    // 서버에 회원가입 요청
    const response = await fetch('/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        full_name: formData.username, // 기본값으로 사용자명 사용
        avatar_url: formData.avatar_url  // 이 부분 추가
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      // 서버에서 오류 응답이 온 경우
      setErrors(prev => ({
        ...prev,
        server: data.message || "회원가입 처리 중 오류가 발생했습니다."
      }));
      setIsSubmitting(false);
      return;
    }

    // 회원가입 성공
    if (data.success) {
      // 자동 로그인 처리
      if (data.user) {
        await login(data.user);
        // 홈페이지로 이동
        navigate("/");
      } else {
        // 사용자 정보가 없으면 로그인 페이지로 이동
        navigate("/login");
      }
      // 모달 닫기
      if (onClose) onClose();
    }
  } catch (error) {
    console.error("회원가입 오류:", error);
    setErrors(prev => ({
      ...prev,
      server: "서버 연결 중 오류가 발생했습니다."
    }));
  } finally {
    setIsSubmitting(false);
  }
}

  return (
    <Container>
      <RegisterCard>
      <CloseButton onClick={() => onClose()}>
        <X size={20} />
      </CloseButton>
        <RegisterHeader>
          <Logo>
            <span>ANTS</span>
            <span>NEST</span>
          </Logo>
          <Subtitle>앤츠네스트에 가입하세요</Subtitle>
        </RegisterHeader>
       
        <RegisterContent>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="username">아이디</Label>
              <InputWrapper>
                <InputIcon>
                  <User size={16} />
                </InputIcon>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="아이디를 입력하세요"
                  value={formData.username}
                  onChange={handleChange}
                  $error={!!errors.username}
                />
              </InputWrapper>
              {errors.username && (
                <ErrorMessage>
                  <AlertCircle size={12} />
                  {errors.username}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">이메일</Label>
              <InputWrapper>
                <InputIcon>
                  <Mail size={16} />
                </InputIcon>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="이메일을 입력하세요"
                  value={formData.email}
                  onChange={handleChange}
                  $error={!!errors.email}
                />
              </InputWrapper>
              {errors.email && (
                <ErrorMessage>
                  <AlertCircle size={12} />
                  {errors.email}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">비밀번호</Label>
              <InputWrapper>
                <InputIcon>
                  <Lock size={16} />
                </InputIcon>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="비밀번호를 입력하세요"
                  value={formData.password}
                  onChange={handleChange}
                  $error={!!errors.password}
                />
                <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggle>
              </InputWrapper>
              {errors.password && (
                <ErrorMessage>
                  <AlertCircle size={12} />
                  {errors.password}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirmPassword">비밀번호 확인</Label>
              <InputWrapper>
                <InputIcon>
                  <Lock size={16} />
                </InputIcon>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="비밀번호를 다시 입력하세요"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  $error={!!errors.confirmPassword}
                />
                <PasswordToggle type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggle>
              </InputWrapper>
              {errors.confirmPassword && (
                <ErrorMessage>
                  <AlertCircle size={12} />
                  {errors.confirmPassword}
                </ErrorMessage>
              )}
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "가입 중..." : "회원가입"}
            </SubmitButton>
          </Form>
          <TermsAgreement>
            가입하시면 앤츠네스트의 <a href="/terms">이용약관</a>과 <a href="/privacy">개인정보처리방침</a>에 동의하게
            됩니다.
          </TermsAgreement>
        </RegisterContent>
      </RegisterCard>
      {errors.server && (
        <ErrorMessage style={{ marginBottom: '16px' }}>
          <AlertCircle size={12} />
          {errors.server}
        </ErrorMessage>
      )}
    </Container>
  )
}

